import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzMessageModule, NzMessageService } from 'ng-zorro-antd/message';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzPageHeaderModule } from 'ng-zorro-antd/page-header';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTabsModule } from 'ng-zorro-antd/tabs';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzToolTipModule } from 'ng-zorro-antd/tooltip';
import { ConsumptionReportOrderResultDto, ConsumptionReportRowDto } from 'src/app/shared/dto/consumption-report-row.dto';
import { ConsumptionReportCandidateDto, ConsumptionReportResolveDto } from 'src/app/shared/dto/consumption-report-resolve.dto';
import { ConsumptionReportService } from '../../services/consumption-report.service';

interface DisplayRow extends ConsumptionReportRowDto {
    difference: number;
}

@Component({
    selector: 'app-consumption-report',
    templateUrl: './consumption-report.component.html',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        NzPageHeaderModule,
        NzInputModule,
        NzButtonModule,
        NzTableModule,
        NzSpinModule,
        NzTagModule,
        NzDividerModule,
        NzIconModule,
        NzModalModule,
        NzMessageModule,
        NzTabsModule,
        NzAlertModule,
        NzToolTipModule,
    ],
})
export class ConsumptionReportComponent implements OnInit {

    // ── Batch / packaging tab ──────────────────────────────────────────
    searchValue: string = '';
    isLoading: boolean = false;
    rows: DisplayRow[] = [];
    headerTitle: string = '';
    headerSubtitle: string = '';
    resolveResult: ConsumptionReportResolveDto | null = null;
    isAmbiguousModalVisible: boolean = false;
    candidates: ConsumptionReportCandidateDto[] = [];

    get totalRaw(): number { return this.rows.reduce((s, r) => s + (r.rawQuantity ?? 0), 0); }
    get totalUsed(): number { return this.rows.reduce((s, r) => s + (r.usedQuantity ?? 0), 0); }
    get totalEstimated(): number { return this.rows.reduce((s, r) => s + (r.estimatedQuantity ?? 0), 0); }
    get totalDifference(): number { return this.totalUsed - this.totalEstimated; }

    // ── Order tab ──────────────────────────────────────────────────────
    orderSearchValue: string = '';
    isLoadingOrder: boolean = false;
    isRecalculating: boolean = false;
    orderRows: DisplayRow[] = [];
    orderHeaderTitle: string = '';
    orderSearchDone: boolean = false;
    currentOrderId: number | null = null;
    orderMatches: ConsumptionReportOrderResultDto[] = [];
    isOrderSelectModalVisible: boolean = false;

    get totalOrderRaw(): number { return this.orderRows.reduce((s, r) => s + (r.rawQuantity ?? 0), 0); }
    get totalOrderUsed(): number { return this.orderRows.reduce((s, r) => s + (r.usedQuantity ?? 0), 0); }
    get totalOrderEstimated(): number { return this.orderRows.reduce((s, r) => s + (r.alternativeEstimatedQuantity ?? r.estimatedQuantity ?? 0), 0); }
    get totalOrderDifference(): number { return this.totalOrderUsed - this.totalOrderEstimated; }
    get totalOrderPackagingQty(): number { return this.orderRows.reduce((s, r) => s + (r.packagingQuantitySum ?? 0), 0); }

    get unloadedMaterialNames(): string[] {
        if (this.orderRows.length === 0) return [];
        const maxLinked = Math.max(...this.orderRows.map(r => r.linkedPackagingsCount ?? 0));
        if (maxLinked <= 1) return [];
        return [...new Set(
            this.orderRows
                .filter(r => (r.rawQuantity ?? 0) > 0 && (r.linkedPackagingsCount ?? 0) <= 1 && maxLinked > 1)
                .map(r => r.articleBatch?.article?.name ?? r.usedPackaging?.packagingType?.name ?? `ID ${r.id}`)
        )];
    }

    constructor(
        private consumptionReportService: ConsumptionReportService,
        private messageService: NzMessageService,
        private modalService: NzModalService,
    ) {}

    ngOnInit(): void {}

    // ── Batch / packaging tab methods ──────────────────────────────────
    async onSearch(): Promise<void> {
        const value = this.searchValue?.trim();
        if (!value) {
            this.messageService.warning('Introduce un número de lote o paquete');
            return;
        }
        this.isLoading = true;
        this.rows = [];
        this.resolveResult = null;
        this.headerTitle = '';
        this.headerSubtitle = '';
        try {
            const result = await this.consumptionReportService.resolve(value);
            this.resolveResult = result;
            if (result.type === 'ambiguous') {
                this.candidates = result.candidates ?? [];
                this.isAmbiguousModalVisible = true;
            } else {
                await this.loadRows(result);
            }
        } catch (error: any) {
            if (error?.status === 404) {
                this.messageService.warning('No se encontró ningún lote ni paquete con ese número');
            } else {
                this.messageService.error('Error al buscar: ' + (error?.message ?? 'desconocido'));
            }
        } finally {
            this.isLoading = false;
        }
    }

    async onCandidateSelected(candidate: ConsumptionReportCandidateDto): Promise<void> {
        this.isAmbiguousModalVisible = false;
        this.isLoading = true;
        try {
            const partial: ConsumptionReportResolveDto = {
                type: candidate.entityType,
                id: candidate.id,
                batchNumber: candidate.batchNumber,
                articleName: candidate.articleName,
            };
            await this.loadRows(partial);
        } catch (error: any) {
            this.messageService.error('Error al cargar datos: ' + (error?.message ?? 'desconocido'));
        } finally {
            this.isLoading = false;
        }
    }

    private async loadRows(result: ConsumptionReportResolveDto): Promise<void> {
        let data: ConsumptionReportRowDto[];
        if (result.type === 'batch') {
            this.headerTitle = `Lote: ${result.batchNumber ?? result.id}`;
            this.headerSubtitle = result.articleName ?? '';
            data = await this.consumptionReportService.getByBatch(result.id!);
        } else {
            this.headerTitle = `Paquete/Bobina: ${result.batchNumber ?? result.id}`;
            this.headerSubtitle = result.articleName ?? '';
            data = await this.consumptionReportService.getByPackaging(result.id!);
        }
        this.rows = data.map(r => ({
            ...r,
            difference: (r.usedQuantity ?? 0) - (r.estimatedQuantity ?? 0),
        }));
    }

    // ── Order tab methods ──────────────────────────────────────────────
    async onOrderSearch(): Promise<void> {
        const value = this.orderSearchValue?.trim();
        if (!value) {
            this.messageService.warning('Introduce un número o descripción de orden');
            return;
        }
        this.isLoadingOrder = true;
        this.orderRows = [];
        this.orderHeaderTitle = '';
        this.orderSearchDone = false;
        try {
            const results = await this.consumptionReportService.searchOrders(value);
            if (results.length === 0) {
                this.orderSearchDone = true;
                this.messageService.warning('No se encontró ninguna orden con ese criterio');
            } else if (results.length === 1) {
                await this.loadOrderRows(results[0]);
            } else {
                this.orderMatches = results;
                this.isOrderSelectModalVisible = true;
            }
        } catch (error: any) {
            this.messageService.error('Error al buscar: ' + (error?.message ?? 'desconocido'));
        } finally {
            this.isLoadingOrder = false;
        }
    }

    async onOrderSelected(order: ConsumptionReportOrderResultDto): Promise<void> {
        this.isOrderSelectModalVisible = false;
        this.isLoadingOrder = true;
        try {
            await this.loadOrderRows(order);
        } catch (error: any) {
            this.messageService.error('Error al cargar datos: ' + (error?.message ?? 'desconocido'));
        } finally {
            this.isLoadingOrder = false;
        }
    }

    private async loadOrderRows(order: ConsumptionReportOrderResultDto): Promise<void> {
        const label = order.externalId ? `#${order.externalId} — ${order.description}` : order.description;
        this.orderHeaderTitle = label;
        this.currentOrderId = order.id;
        const data = await this.consumptionReportService.getByOrder(order.id);
        this.orderRows = data.map(r => ({
            ...r,
            difference: (r.usedQuantity ?? 0) - (r.alternativeEstimatedQuantity ?? r.estimatedQuantity ?? 0),
        }));
        this.orderSearchDone = true;
    }

    async recalcularOrden(): Promise<void> {
        if (!this.currentOrderId) return;
        this.isRecalculating = true;
        try {
            await this.consumptionReportService.recalculateByOrder(this.currentOrderId);
            const data = await this.consumptionReportService.getByOrder(this.currentOrderId);
            this.orderRows = data.map(r => ({
                ...r,
                difference: (r.usedQuantity ?? 0) - (r.alternativeEstimatedQuantity ?? r.estimatedQuantity ?? 0),
            }));
            this.messageService.success('Consumos recalculados correctamente');
        } catch (error: any) {
            this.messageService.error('Error al recalcular: ' + (error?.message ?? 'desconocido'));
        } finally {
            this.isRecalculating = false;
        }
    }

    // ── Shared helpers ─────────────────────────────────────────────────
    getPackagingLabel(row: DisplayRow): string {
        if (!row.usedPackaging) return '—';
        return row.usedPackaging.batchNumber
            ?? row.usedPackaging.externalBatchNumber
            ?? String(row.usedPackaging.id);
    }

    getDifferenceColor(diff: number): string {
        if (diff > 0) return 'red';
        if (diff < 0) return 'green';
        return 'default';
    }

    getSensorMeters(row: ConsumptionReportRowDto & { difference: number }): string {
        if (row.motivoAncho && row.medidaCorte && row.rawQuantity) {
            const meters = (row.rawQuantity / row.motivoAncho) * (row.medidaCorte / 1000);
            return meters.toFixed(2);
        }
        return '—';
    }
}

